// src/DimensionnementGenerateurModal.js
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Zap, AlertTriangle } from 'lucide-react';

const getProductImage = (ref) => `https://devis.takoussane.com/images/${ref}.png`;


const APPAREILS_DEFAUT = [
  { id: 'lampe_led',     nom: 'Lampe LED',                     puissance: 10,   heures: 5,  icone: '💡' },
  { id: 'tv_32',         nom: 'TV 32"',                        puissance: 60,   heures: 6,  icone: '📺' },
  { id: 'tv_43',         nom: 'TV 43"',                        puissance: 80,   heures: 6,  icone: '📺' },
  { id: 'frigo',         nom: 'Réfrigérateur',                 puissance: 150,  heures: 24, icone: '🧊' },
  { id: 'congelateur',   nom: 'Congélateur',                   puissance: 200,  heures: 24, icone: '🧊' },
  { id: 'clim_9000',     nom: 'Climatiseur 9000 BTU (0.75CV)', puissance: 900,  heures: 8,  icone: '❄️' },
  { id: 'clim_12000',    nom: 'Climatiseur 12000 BTU (1CV)',   puissance: 1200, heures: 8,  icone: '❄️' },
  { id: 'clim_18000',    nom: 'Climatiseur 18000 BTU (1.5CV)', puissance: 1800, heures: 8,  icone: '❄️' },
  { id: 'clim_24000',    nom: 'Climatiseur 24000 BTU (2CV)',   puissance: 2400, heures: 8,  icone: '❄️' },
  { id: 'ventilateur',   nom: 'Ventilateur',                   puissance: 60,   heures: 8,  icone: '🌀' },
  { id: 'chargeur',      nom: 'Chargeur téléphone',            puissance: 10,   heures: 3,  icone: '📱' },
  { id: 'decodeur',      nom: 'Décodeur TV',                   puissance: 20,   heures: 6,  icone: '📡' },
  { id: 'fer',           nom: 'Fer à repasser',                puissance: 1000, heures: 1,  icone: '👔' },
  { id: 'machine_laver', nom: 'Machine à laver',               puissance: 500,  heures: 1,  icone: '🫧' },
  { id: 'ordinateur',    nom: 'Ordinateur / PC',               puissance: 100,  heures: 6,  icone: '💻' },
  { id: 'pompe_eau',     nom: 'Pompe à eau (surface)',         puissance: 500,  heures: 2,  icone: '💧' },
];

const GENERATEURS = [
  { ref: 'S-600',  nom: 'Générateur Solaire Mobile 600W',   puissance: 600,  peak: 1000, prix: 445000,  couleur: '#0284c7', note: 'Usage domestique léger — bureau, lumières, TV' },
  { ref: 'S-1200E', nom: 'Générateur Solaire Mobile 1200W',  puissance: 1200, peak: 3600, prix: 742000,  couleur: '#0284c7', note: 'Clim 9000 BTU + appareils ménagers courants' },
  { ref: 'S-1800E', nom: 'Générateur Solaire Mobile 1800W',  puissance: 1800, peak: 4000, prix: 1039000, couleur: '#059669', note: 'Clim 12000 BTU + frigo + lumières' },
  { ref: 'S-2400E', nom: 'Générateur Solaire Mobile 2400W',  puissance: 2400, peak: 5000, prix: 1452000, couleur: '#d97706', note: 'Clim 18000 BTU ou multi-appareils' },
  { ref: 'S-3600E', nom: 'Générateur Solaire Mobile 3600W',  puissance: 3600, peak: 6000, prix: 2265000, couleur: '#7c3aed', note: 'Clim 24000 BTU ou usage intensif' },
  { ref: 'S-5000E', nom: 'Générateur Solaire Mobile 5000W',  puissance: 5000, peak: 7000, prix: 3820000, couleur: '#dc2626', note: 'Multi-clims ou usage professionnel' },
];

const CAPACITE_BAT = {
  'S-600': '595Wh', 'S-1200E': '992Wh', 'S-1800E': '1488Wh',
  'S-2400E': '2232Wh', 'S-3600E': '3000Wh', 'S-5000E': '5040Wh',
};

const PANNEAUX = [
  { ref: 'PV-100U1', nom: 'Panneau Solaire 100W',  puissance: 100, prix: 164000 },
  { ref: 'PV-100U2', nom: 'Panneau Solaire 200W',  puissance: 200, prix: 320000 },
  { ref: 'PV-220U1', nom: 'Panneau Solaire 220W',  puissance: 220, prix: 336000 },
  { ref: 'PV-220U2', nom: 'Panneau Solaire 440W',  puissance: 440, prix: 633000 },
];

const APPAREILS_MOTEUR = ['clim_9000','clim_12000','clim_18000','clim_24000','frigo','congelateur','machine_laver','pompe_eau'];

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const isMoteur = (a) => {
  if (['clim_9000','clim_12000','clim_18000','clim_24000','frigo','congelateur','machine_laver','pompe_eau'].includes(a.appareilId)) return true;
  const nom = (a.nom || '').toLowerCase();
  return nom.includes('clim') || nom.includes('climatiseur') || nom.includes('pompe') || nom.includes('frigo') || nom.includes('congé') || nom.includes('machine');
};
const getMargeMoteur = (a) => {
  const nom = (a.nom || '').toLowerCase();
  if (nom.includes('clim') || nom.includes('climatiseur')) return 4.5;
  if (nom.includes('frigo') || nom.includes('cong')) return 2.5;
  if (nom.includes('pompe') || nom.includes('machine')) return 2.0;
  if (['clim_9000','clim_12000','clim_18000','clim_24000'].includes(a.appareilId)) return 4.5;
  if (['frigo','congelateur'].includes(a.appareilId)) return 2.5;
  if (['pompe_eau','machine_laver'].includes(a.appareilId)) return 2.0;
  return 1.5;
};
const selectionnerGenerateur = (appareils) => {
  const puissanceNominale = appareils.reduce((sum, a) => sum + (a.puissance || 0) * (a.quantite || 1), 0);
  const picDemarrage = appareils.reduce((sum, a) => {
    const marge = isMoteur(a) ? getMargeMoteur(a) : 1.0;
    return sum + (a.puissance || 0) * (a.quantite || 1) * marge;
  }, 0);
  return GENERATEURS.find(g => g.puissance >= puissanceNominale && g.peak >= picDemarrage) || GENERATEURS[GENERATEURS.length - 1];
};

const selectionnerPanneaux = (gen) => {
  if (gen.puissance >= 2400) return { panneau: PANNEAUX[3], nb: 1 };
  if (gen.puissance >= 1200) return { panneau: PANNEAUX[2], nb: 1 };
  return { panneau: PANNEAUX[0], nb: 1 };
};

const DimensionnementGenerateurModal = ({ onClose, onGenererDevis }) => {
  const [etape, setEtape] = useState(1);
  useEffect(() => {
    const refsGen = GENERATEURS.map(g => g.ref);
    const refsPv = PANNEAUX.map(p => p.ref);
    supabase.from('catalogue_produits').select('ref,nom,prix').in('ref', [...refsGen, ...refsPv, 'PRESTA-INSTALL-PV']).eq('actif', true)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(p => { map[p.ref] = p; });
        if (map['PRESTA-INSTALL-PV']) setInstallNom(map['PRESTA-INSTALL-PV'].nom);
      GENERATEURS.forEach(g => { if (map[g.ref]) { g.prix = map[g.ref].prix ?? g.prix; g.nom = map[g.ref].nom || g.nom; } });
        PANNEAUX.forEach(p => { if (map[p.ref]) { p.prix = map[p.ref].prix ?? p.prix; p.nom = map[p.ref].nom || p.nom; } });
      });
  }, []);
  const [appareils, setAppareils] = useState([
    { id: Date.now(), appareilId: 'lampe_led', nom: 'Lampe LED', puissance: 10, heures: 5, quantite: 4, icone: '💡' },
  ]);
  const [appareilsSelection, setAppareilsSelection] = useState({});
  const [installNom, setInstallNom] = useState('Installation et mise en service');
  const [resultat, setResultat] = useState(null);
  const [alerte, setAlerte] = useState(null);
  const [showIA, setShowIA] = useState(false);
  const [promptIA, setPromptIA] = useState('');
  const [loadingIA, setLoadingIA] = useState(false);
  const [genChoisi, setGenChoisi] = useState(null);

  const puissancePointe = appareils.reduce((sum, a) => sum + (a.puissance || 0) * (a.quantite || 1), 0);
  const consoJournaliere = appareils.reduce((sum, a) => sum + (a.puissance || 0) * (a.heures || 0) * (a.quantite || 1), 0);

  const analyserIA = async () => {
    if (!promptIA.trim()) return;
    setLoadingIA(true);
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer 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' },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [{
            role: 'system',
            content: `Tu es un expert en générateurs solaires au Sénégal. Analyse la description et retourne UNIQUEMENT un JSON valide:
{"appareils": [{"id": "string", "nom": "string", "puissance": number, "heures": number, "quantite": number}]}
Appareils courants: Lampe LED=10W/5h, TV 32"=60W/6h, Réfrigérateur=150W/24h, Clim 9000BTU=900W/8h, Clim 12000BTU=1200W/8h, Ventilateur=60W/8h, Ordinateur=100W/6h, Chargeur=10W/3h, Fer=1000W/1h, Pompe eau=500W/2h.`
          }, { role: 'user', content: promptIA }],
          temperature: 0.1, max_tokens: 800
        })
      });
      const data = await res.json();
      let content = data.choices?.[0]?.message?.content || '{}';
      content = content.replace(/```json/g,'').replace(/```/g,'').trim();
      const parsed = JSON.parse(content);
      if (parsed.appareils?.length) {
        setAppareils(parsed.appareils.map(a => ({ ...a, id: a.id || (Date.now()+Math.random()).toString() })));
        setShowIA(false); setPromptIA('');
      }
    } catch(e) { console.error('IA generateur:', e); }
    setLoadingIA(false);
  };

  const allerEtape2 = () => {
    if (puissancePointe === 0) return;
    if (puissancePointe > 4000) { setAlerte('trop_puissant'); return; }
    setAlerte(null);
    const gen = selectionnerGenerateur(appareils);
    setGenChoisi(gen);
    const { panneau, nb } = selectionnerPanneaux(gen);
    const aMoteur = appareils.some(a => APPAREILS_MOTEUR.includes(a.appareilId));
    setResultat({ gen, panneau, nbPanneaux: nb, puissancePointe, consoJournaliere, aMoteur });
    setEtape(2);
  };

  const genererDevis = () => {
    if (!resultat) return;
    const articles = [];
    let uid = 0;
    const peak = resultat.gen.peak;
    const nominal = resultat.gen.puissance;

    // Titre — contient AMOTEUR comme marqueur pour detectGenerateur
    articles.push({
      _uid: uid++, _titre: true,
      description: 'KIT GENERATEUR SOLAIRE MOBILE TAKOUSSANE ENERGY  —  ' + nominal + 'W  |  Puissance de pointe : ' + peak + 'W  |  Ref: ' + resultat.gen.ref,
      _meta: 'ClientW:' + resultat.puissancePointe + '|aMoteur:' + (resultat.aMoteur ? '1' : '0') + '|Peak:' + peak + '|Nominal:' + nominal,
      quantity: null, unitPrice: null, image: null,
    });

    // Description article générateur — conforme au spec
    const picEstime = Math.round(resultat.puissancePointe * 1.5);
    articles.push({
      _uid: uid++,
      description:
        (GENERATEURS.find(g => g.ref === resultat.gen.ref)?.nom || resultat.gen.nom) + '\n' +
        'Ref: ' + resultat.gen.ref + '  |  Puissance de pointe : ' + peak + 'W  |  Batterie : ' + (CAPACITE_BAT[resultat.gen.ref] || '') + ' LiFePO4\n' +
        'Puissance équipements client : ' + resultat.puissancePointe + 'W' +
        (resultat.aMoteur
          ? '  |  Pic de démarrage estimé : ~' + picEstime + 'W (couvert par la pointe)\n' +
            String.fromCharCode(9888) + 'Ne pas dépasser ' + nominal + 'W en continu  |  Garantie 2 ans'
          : '\nGarantie 2 ans'),
      quantity: 1,
      unitPrice: resultat.gen.prix,
      image: getProductImage(resultat.gen.ref),
    });

    // Panneau(x)
    if (resultat.nbPanneaux > 0) {
      articles.push({
        _uid: uid++,
        description: (PANNEAUX.find(p => p.ref === resultat.panneau.ref)?.nom || resultat.panneau.nom) + '\nRef: ' + resultat.panneau.ref,
        quantity: resultat.nbPanneaux,
        unitPrice: resultat.panneau.prix,
        image: getProductImage(resultat.panneau.ref),
      });
    }

    // Installation
    articles.push({
      _uid: uid++,
      description: installNom,
      quantity: 1,
      unitPrice: 15000,
      image: getProductImage('PRESTA-INSTALL-PV'),
    });

    onGenererDevis(articles);
    onClose();
  };

  const totalResultat = resultat
    ? resultat.gen.prix + (resultat.nbPanneaux * resultat.panneau.prix) + 15000
    : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '900px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>⚡ Kit Générateur PV</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px', marginTop: '2px' }}>
              {etape === 1 ? 'Étape 1 — Appareils à alimenter' : 'Étape 2 — Kit dimensionné'}
            </div>
          </div>
          <button onClick={() => setShowIA(!showIA)} style={{ background: showIA ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : 'white', border: 'none', borderRadius: '8px', padding: '6px 14px', color: showIA ? 'white' : '#7c3aed', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>✨ IA</button>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {[1, 2].map(n => (
              <div key={n} style={{ width: '28px', height: '28px', borderRadius: '50%', background: etape >= n ? 'white' : 'rgba(255,255,255,0.3)', color: etape >= n ? '#d97706' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>{n}</div>
            ))}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', borderRadius: '6px', padding: '6px', marginLeft: '8px', display: 'flex' }}><X size={20} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {etape === 1 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>Quels appareils le client souhaite-t-il alimenter ?</div>
              {showIA && (
                <div style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '16px', marginTop: '12px' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#5b21b6', marginBottom: '4px' }}>✨ Décris le projet, l’IA s’occupe du reste</div>
                  <div style={{ fontSize: '11px', color: '#7c3aed', marginBottom: '10px' }}>Ex: maison 3 pièces à Dakar, 1 clim 9000 BTU, 1 frigo, 6 lampes LED, télévision</div>
                  <textarea value={promptIA} onChange={e => setPromptIA(e.target.value)} rows={2}
                    placeholder="Décris les équipements à alimenter..."
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '8px', fontSize: '13px', outline: 'none', background: 'white', resize: 'none', fontFamily: 'inherit' }} />
                  <button onClick={analyserIA} disabled={loadingIA || !promptIA.trim()}
                    style={{ marginTop: '8px', width: '100%', padding: '9px', background: loadingIA ? '#a78bfa' : 'linear-gradient(135deg,#7c3aed,#a855f7)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: 700, cursor: loadingIA ? 'wait' : 'pointer' }}>
                    {loadingIA ? '⏳ Analyse...' : '✨ Analyser et générer la liste'}
                  </button>
                </div>
              )}
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Le générateur adapté sera sélectionné automatiquement selon la puissance de pointe.</div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 14px', textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Puissance de pointe</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#d97706' }}>{fmt(puissancePointe)} W</div>
                  </div>
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '8px 14px', textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Conso journalière</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#0284c7' }}>{fmt(consoJournaliere)} Wh/j</div>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {alerte === 'trop_puissant' && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <AlertTriangle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#dc2626', marginBottom: '4px' }}>Puissance trop élevée pour un générateur portable</div>
                      <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                        La puissance de <strong>{fmt(puissancePointe)} W</strong> dépasse 4000W. Utilisez plutôt un <strong>Starter Kit PV</strong>.
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 70px 40px', gap: '8px', padding: '6px 10px', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <div>Appareil</div><div style={{ textAlign: 'center' }}>Puissance W</div><div style={{ textAlign: 'center' }}>Heures/j</div><div style={{ textAlign: 'center' }}>Qté</div><div></div>
                </div>
                {appareils.map(a => (
                  <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 70px 40px', gap: '8px', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{a.icone}</span>
                      <input value={a.nom} onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, nom: e.target.value } : ap))}
                        style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', background: 'white' }} />
                    </div>
                    <input type="number" value={a.puissance} min="1" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, puissance: parseFloat(e.target.value) || 0 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <input type="number" value={a.heures} min="0.5" max="24" step="0.5" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, heures: parseFloat(e.target.value) || 0 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <input type="number" value={a.quantite} min="1" onChange={e => setAppareils(prev => prev.map(ap => ap.id === a.id ? { ...ap, quantite: parseInt(e.target.value) || 1 } : ap))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', textAlign: 'center', width: '100%' }} />
                    <button onClick={() => setAppareils(prev => prev.filter(ap => ap.id !== a.id))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Appareils à alimenter — cliquez pour sélectionner</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                  {APPAREILS_DEFAUT.map(app => {
                    const sel = appareilsSelection[app.id] || 0;
                    const selected = sel > 0;
                    return (
                      <div key={app.id} onClick={() => {
                        if (!selected) setAppareilsSelection(prev => ({ ...prev, [app.id]: 1 }));
                        else setAppareilsSelection(prev => { const n = { ...prev }; delete n[app.id]; return n; });
                      }}
                        style={{ border: '2px solid ' + (selected ? '#d97706' : '#e5e7eb'), borderRadius: '8px', padding: '7px 5px', cursor: 'pointer', background: selected ? '#fffbeb' : 'white', transition: 'all 0.15s', position: 'relative', userSelect: 'none' }}>
                        {selected && (
                          <div style={{ position: 'absolute', top: '3px', right: '3px', width: '13px', height: '13px', borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: 'white', fontSize: '8px', fontWeight: 700 }}>✓</span>
                          </div>
                        )}
                        <div style={{ fontSize: '16px', textAlign: 'center', marginBottom: '2px' }}>{app.icone}</div>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#1f2937', textAlign: 'center', lineHeight: 1.2 }}>{app.nom}</div>
                        <div style={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', marginTop: '1px' }}>{app.puissance}W·{app.heures}h/j</div>
                        {selected && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '5px' }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => setAppareilsSelection(prev => ({ ...prev, [app.id]: Math.max(1, (prev[app.id] || 1) - 1) }))}
                              style={{ width: '16px', height: '16px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '11px', lineHeight: 1, padding: 0 }}>−</button>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#d97706', minWidth: '14px', textAlign: 'center' }}>{sel}</span>
                            <button onClick={() => setAppareilsSelection(prev => ({ ...prev, [app.id]: (prev[app.id] || 1) + 1 }))}
                              style={{ width: '16px', height: '16px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '11px', lineHeight: 1, padding: 0 }}>+</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {Object.keys(appareilsSelection).length > 0 && (
                  <button onClick={() => {
                    Object.entries(appareilsSelection).forEach(([id, qty]) => {
                      const app = APPAREILS_DEFAUT.find(a => a.id === id);
                      if (app) for (let i = 0; i < qty; i++) setAppareils(prev => [...prev, { id: Date.now() + Math.random(), appareilId: app.id, nom: app.nom, puissance: app.puissance, heures: app.heures, quantite: 1, icone: app.icone }]);
                    });
                    setAppareilsSelection({});
                  }}
                    style={{ width: '100%', padding: '9px', borderRadius: '8px', background: '#d97706', color: 'white', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                    ✓ Ajouter {Object.values(appareilsSelection).reduce((s, v) => s + v, 0)} appareil(s) sélectionné(s)
                  </button>
                )}
                <button onClick={() => setAppareils(prev => [...prev, { id: Date.now(), appareilId: 'custom', nom: 'Appareil personnalisé', puissance: 100, heures: 4, quantite: 1, icone: '⚡' }])}
                  style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', width: 'fit-content' }}>
                  <Plus size={14} /> Appareil personnalisé
                </button>
              </div>

              {puissancePointe > 0 && puissancePointe <= 4000 && (
                <div style={{ marginTop: '16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px' }}>
                  <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>⚡ Générateur recommandé pour cette configuration</div>
                  {(() => {
                    const g = selectionnerGenerateur(appareils);
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e' }}>{g.nom}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{g.note} — Pointe fabricant : {g.peak}W</div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '16px', color: '#d97706' }}>{fmt(g.prix)} FCFA</div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {etape === 2 && resultat && (
            <div>
              <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e', marginBottom: '10px' }}>
                  ✅ Kit Générateur Solaire — {resultat.gen.puissance}W
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { label: 'Puissance client', value: fmt(resultat.puissancePointe) + ' W' },
                    { label: 'Pointe fabricant', value: fmt(resultat.gen.peak) + ' W' },
                    { label: 'Panneau solaire', value: resultat.nbPanneaux + ' × ' + resultat.panneau.puissance + 'W' },
                  ].map(item => (
                    <div key={item.label} style={{ background: 'white', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.label}</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#d97706' }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Générateur sélectionné — modifiable</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {GENERATEURS.map(g => {
                    const selected = genChoisi?.ref === g.ref;
                    const recommande = g.ref === selectionnerGenerateur(appareils).ref;
                    return (
                      <div key={g.ref} onClick={() => {
                        setGenChoisi(g);
                        const { panneau, nb } = selectionnerPanneaux(g);
                        setResultat(prev => ({ ...prev, gen: g, panneau, nbPanneaux: nb }));
                      }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', border: '2px solid ' + (selected ? g.couleur : '#e5e7eb'), borderRadius: '10px', background: selected ? g.couleur + '10' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: g.couleur + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>⚡</div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 700, fontSize: '13px', color: selected ? g.couleur : '#1f2937' }}>{g.nom}</span>
                              {recommande && <span style={{ background: '#d97706', color: 'white', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px' }}>Recommandé</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{g.note} — Nominal: {g.puissance}W — Pointe: {g.peak}W</div>
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: selected ? g.couleur : '#374151' }}>{fmt(g.prix)} FCFA</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Composition du kit</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#fffbeb', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>⚡ {resultat.gen.nom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>1</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(resultat.gen.prix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#d97706' }}>{fmt(resultat.gen.prix)} F</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#fef3c7', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>🔆 {resultat.panneau.nom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>{resultat.nbPanneaux}</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(resultat.panneau.prix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#d97706' }}>{fmt(resultat.nbPanneaux * resultat.panneau.prix)} F</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '12px', color: '#374151' }}>🔧 Installation et mise en service</div>
                  <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600 }}>1</div>
                  <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>15 000 F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#d97706' }}>15 000 F</div>
                </div>
              </div>

              <div style={{ marginTop: '16px', background: '#d97706', borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Total kit estimé</span>
                <span style={{ color: 'white', fontWeight: 800, fontSize: '20px' }}>{fmt(totalResultat)} FCFA</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <div>
            {etape > 1 && (
              <button onClick={() => setEtape(etape - 1)}
                style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                ← Retour
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>Fermer</button>
            {etape === 1 && (
              <button onClick={allerEtape2} disabled={appareils.length === 0 || puissancePointe === 0}
                style={{ background: appareils.length > 0 && puissancePointe > 0 ? '#d97706' : '#e5e7eb', color: appareils.length > 0 && puissancePointe > 0 ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Zap size={14} /> Dimensionner →
              </button>
            )}
            {etape === 2 && (
              <button onClick={genererDevis}
                style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📋 Générer le devis
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DimensionnementGenerateurModal;
